import jStat from 'jstat';
import {
  GREATER_THAN_VALUE,
  LESS_THAN_VALUE
} from 'scenes/InferentialStatistics/Constants';
import { makeOneSidedPValue, makeTwoSidedPValue, getPHat, makeProportionStDev } from './oneSampleProportion';
import { round6 } from 'utilities/math';

export const makeDfProportions = (s1, s2, t1, t2) => (s1 / t1) - (s2 / t2);
export const makeProportionStDevCommon = (p, n1, n2) => Math.sqrt((p * (1 - p)) * ((1 / n1) + (1 / n2)));
export const makeProportionStDevConfInterval = (n1, n2, p1, p2) =>
  Math.sqrt((p1 * (1 - p1) / n1) + (p2 * (1 - p2) / n2));

export const makeTwoProportionHypothesizedProportion = ({
  numberOfSuccessFirst,
  numberOfSuccessSecond,
  numberOfTrialsFirst,
  numberOfTrialsSecond,
  dfHypothesisProportion,
  alternativeHypothesis
}) => {
  const dfProportions = makeDfProportions(numberOfSuccessFirst, numberOfSuccessSecond, numberOfTrialsFirst, numberOfTrialsSecond);

  const populationProportionCommon = (+numberOfSuccessFirst + +numberOfSuccessSecond) / (+numberOfTrialsFirst + +numberOfTrialsSecond);

  const standardError = makeProportionStDevCommon(populationProportionCommon, +numberOfTrialsFirst, +numberOfTrialsSecond);
  let zStatistics = (dfProportions - dfHypothesisProportion) / standardError;

  if (!isFinite(zStatistics)) {
    zStatistics = 0;
  }

  let pValue = 0;

  switch (alternativeHypothesis) {
    case GREATER_THAN_VALUE:
      zStatistics > 0
        ? (pValue = makeOneSidedPValue(zStatistics))
        : (pValue = 1 - makeOneSidedPValue(zStatistics));
      break;
    case LESS_THAN_VALUE:
      zStatistics < 0
        ? (pValue = makeOneSidedPValue(zStatistics))
        : (pValue = 1 - makeOneSidedPValue(zStatistics));
      break;

    default:
      pValue = makeTwoSidedPValue(zStatistics);
  }

  return { h0: dfHypothesisProportion, h1: dfHypothesisProportion, tStatistics: zStatistics, dfValue: dfProportions, pValue, standardError, populationProportionCommon };
};

export const makeTwoProportionConfidenceLimits = ({
  numberOfSuccessFirst,
  numberOfSuccessSecond,
  numberOfTrialsFirst,
  numberOfTrialsSecond,
  confidenceInterval
}) => {

  const dfProportions = makeDfProportions(numberOfSuccessFirst, numberOfSuccessSecond, numberOfTrialsFirst, numberOfTrialsSecond);
  const pHat1 = numberOfSuccessFirst / numberOfTrialsFirst;
  const pHat2 = numberOfSuccessSecond / numberOfTrialsSecond;

  const standardError = makeProportionStDevConfInterval(+numberOfTrialsFirst, +numberOfTrialsSecond, pHat1, pHat2);

  const confidenceLevel = 1 - ((1 - (confidenceInterval / 100)) / 2);
  const zScore = jStat.normal.inv(confidenceLevel, 0, 1);

  const lowerLimit = dfProportions - (zScore * standardError);
  const upperLimit = dfProportions + (zScore * standardError);

  return { lowerLimit, upperLimit, dfValue: dfProportions, standardError };
};

export const calculateTableDataForTwoSampleProportion = ({
  numberOfSuccessFirst,
  numberOfSuccessSecond,
  numberOfTrialsFirst,
  numberOfTrialsSecond,
  firstVariable,
  secondVariable,
  successGroupFirst,
  successGroupSecond
}, isGroupChosen) => {
  const pHat1 = getPHat(numberOfSuccessFirst, numberOfTrialsFirst);
  const pHat2 = getPHat(numberOfSuccessSecond, numberOfTrialsSecond);
  const standardError1 = makeProportionStDev(pHat1, numberOfTrialsFirst);
  const standardError2 = makeProportionStDev(pHat2, numberOfTrialsSecond);
  const firstName = firstVariable ? firstVariable : 'Sample #1';
  const secondName = secondVariable ? secondVariable : 'Sample #2';
  let firstLevelName = [];
  let secondLevelName = [];
  if (isGroupChosen) {
    firstLevelName = [successGroupFirst ? successGroupFirst : 'Sample #1'];
    secondLevelName = [successGroupSecond ? successGroupSecond : 'Sample #2'];
  }

  return [
    [firstName, ...firstLevelName, numberOfTrialsFirst, numberOfSuccessFirst, pHat1, round6(standardError1)],
    [secondName, ...secondLevelName, numberOfTrialsSecond, numberOfSuccessSecond, pHat2, round6(standardError2)]
  ];
};
